import { enableMapSet } from "immer";
import { create } from "zustand";
import { immer } from "zustand/middleware/immer";
import { BREAKPOINTS } from "@perceptron/utils/window";
import { generateUUID } from "@perceptron/utils/crypto";

import { generateMediaKey } from "../utils/annotationUtils";
import { NORMALIZED_BOUNDS } from "../utils/coordinates";
import { EditorAnnotation } from "./annotationTypes";

// Enable Map/Set support in immer
enableMapSet();

interface AnnotationHistory {
  past: EditorAnnotation[][];
  present: EditorAnnotation[];
  future: EditorAnnotation[][];
}

interface AnnotationStore {
  // Single source of truth: Map by media key for performance
  annotationsByMedia: Map<string, EditorAnnotation[]>;

  // Current active media (for focused editing)
  activeMediaKey: string | null;
  activeMediaSetId: string | null;

  // UI state
  hoveredAnnotationId: string | null;
  autoEditAnnotationId: string | null;

  // History per media
  history: Map<string, AnnotationHistory>;

  // Core CRUD operations
  addAnnotation: (annotation: Omit<EditorAnnotation, "id">) => void;
  updateAnnotation: (id: string, updates: Partial<EditorAnnotation>) => void;
  deleteAnnotation: (id: string) => void;
  clearAnnotations: () => void;

  // Media-aware operations
  setActiveMedia: (mediaSetId: string | null, mediaKey: string | null) => void;
  getAnnotations: (mediaSetId?: string, mediaKey?: string) => EditorAnnotation[];
  clearMediaAnnotations: (mediaSetId: string, mediaKey: string) => void;
  setAnnotationsForMedia: (mediaSetId: string, mediaKey: string, annotations: EditorAnnotation[]) => void;

  // UI state operations
  setHoveredAnnotationId: (id: string | null) => void;
  clearAutoEditAnnotationId: () => void;

  // Undo/Redo
  undo: () => void;
  redo: () => void;
  canUndo: () => boolean;
  canRedo: () => boolean;
}

// Helper to clean up floating-point precision errors in annotation coordinates
function cleanAnnotationValues<T extends Partial<EditorAnnotation>>(values: T): T {
  const cleaned = { ...values };

  if (typeof cleaned.x === "number") {
    cleaned.x = Math.round(cleaned.x);
    cleaned.x = Math.max(NORMALIZED_BOUNDS.MIN, Math.min(NORMALIZED_BOUNDS.MAX, cleaned.x));
  }

  if (typeof cleaned.y === "number") {
    cleaned.y = Math.round(cleaned.y);
    cleaned.y = Math.max(NORMALIZED_BOUNDS.MIN, Math.min(NORMALIZED_BOUNDS.MAX, cleaned.y));
  }

  if (typeof cleaned.width === "number") {
    cleaned.width = Math.round(cleaned.width);
    cleaned.width = Math.max(1, Math.min(NORMALIZED_BOUNDS.MAX, cleaned.width));
  }

  if (typeof cleaned.height === "number") {
    cleaned.height = Math.round(cleaned.height);
    cleaned.height = Math.max(1, Math.min(NORMALIZED_BOUNDS.MAX, cleaned.height));
  }

  return cleaned;
}

export const useAnnotationStore = create<AnnotationStore>()(
  immer((set, get) => ({
    annotationsByMedia: new Map(),
    activeMediaKey: null,
    activeMediaSetId: null,
    hoveredAnnotationId: null,
    autoEditAnnotationId: null,
    history: new Map(),

    setActiveMedia: (mediaSetId, mediaKey) => {
      set((state) => {
        state.activeMediaSetId = mediaSetId;
        state.activeMediaKey = mediaKey;
      });
    },

    getAnnotations: (mediaSetId?, mediaKey?) => {
      const state = get();

      // If no params, use active media
      const setId = mediaSetId ?? state.activeMediaSetId;
      const key = mediaKey ?? state.activeMediaKey;

      if (!setId || !key) {
        return [];
      }

      const compositeKey = `${setId}:${key}`;
      return state.annotationsByMedia.get(compositeKey) || [];
    },

    clearMediaAnnotations: (mediaSetId, mediaKey) => {
      const key = `${mediaSetId}:${mediaKey}`;
      set((state) => {
        state.annotationsByMedia.set(key, []);
        state.history.set(key, {
          past: [],
          present: [],
          future: [],
        });
      });
    },

    setHoveredAnnotationId: (id) => {
      set((state) => {
        state.hoveredAnnotationId = id;
      });
    },

    clearAutoEditAnnotationId: () => {
      set((state) => {
        state.autoEditAnnotationId = null;
      });
    },

    addAnnotation: (annotation) => {
      const state = get();

      // Require active media to be set
      if (!state.activeMediaSetId || !state.activeMediaKey) {
        console.error("Cannot add annotation: no active media set. Call setActiveMedia first.");
        return;
      }

      // Clean the annotation values before adding
      const cleanedAnnotation = cleanAnnotationValues(annotation);
      const mediaKey = generateMediaKey(state.activeMediaSetId, state.activeMediaKey);

      const newAnnotation: EditorAnnotation = {
        ...cleanedAnnotation,
        id: generateUUID(),
        mediaSetId: state.activeMediaSetId,
        mediaKey: state.activeMediaKey,
      };

      // Get current annotations for this media
      const current = state.annotationsByMedia.get(mediaKey) || [];
      const updated = [...current, newAnnotation];

      // Update history for this media
      const mediaHistory = state.history.get(mediaKey) || {
        past: [],
        present: [],
        future: [],
      };

      set((state) => {
        state.annotationsByMedia.set(mediaKey, updated);
        state.history.set(mediaKey, {
          past: [...mediaHistory.past, current],
          present: updated,
          future: [],
        });
        // Set the new annotation for auto-edit (only on desktop where sidebar is visible)
        // Check if viewport is at least mobile breakpoint
        if (typeof window !== "undefined" && window.innerWidth >= BREAKPOINTS.MOBILE) {
          state.autoEditAnnotationId = newAnnotation.id;
        }
      });
    },

    updateAnnotation: (id, updates) => {
      const state = get();

      if (!state.activeMediaSetId || !state.activeMediaKey) {
        console.error("Cannot update annotation: no active media set");
        return;
      }

      // Clean the update values
      const cleanedUpdates = cleanAnnotationValues(updates);
      const mediaKey = generateMediaKey(state.activeMediaSetId, state.activeMediaKey);

      const current = state.annotationsByMedia.get(mediaKey) || [];
      const updated = current.map((ann) => (ann.id === id ? { ...ann, ...cleanedUpdates } : ann));

      const mediaHistory = state.history.get(mediaKey) || {
        past: [],
        present: current,
        future: [],
      };

      set((state) => {
        state.annotationsByMedia.set(mediaKey, updated);
        state.history.set(mediaKey, {
          past: [...mediaHistory.past, current],
          present: updated,
          future: [],
        });
      });
    },

    deleteAnnotation: (id) => {
      const state = get();

      if (!state.activeMediaSetId || !state.activeMediaKey) {
        console.error("Cannot delete annotation: no active media set");
        return;
      }

      const mediaKey = generateMediaKey(state.activeMediaSetId, state.activeMediaKey);
      const current = state.annotationsByMedia.get(mediaKey) || [];
      const updated = current.filter((ann) => ann.id !== id);

      const mediaHistory = state.history.get(mediaKey) || {
        past: [],
        present: current,
        future: [],
      };

      set((state) => {
        state.annotationsByMedia.set(mediaKey, updated);
        state.history.set(mediaKey, {
          past: [...mediaHistory.past, current],
          present: updated,
          future: [],
        });
      });
    },

    clearAnnotations: () => {
      const state = get();

      if (!state.activeMediaSetId || !state.activeMediaKey) {
        console.error("Cannot clear annotations: no active media set");
        return;
      }

      const mediaKey = generateMediaKey(state.activeMediaSetId, state.activeMediaKey);
      const current = state.annotationsByMedia.get(mediaKey) || [];

      const mediaHistory = state.history.get(mediaKey) || {
        past: [],
        present: current,
        future: [],
      };

      set((state) => {
        state.annotationsByMedia.set(mediaKey, []);
        state.history.set(mediaKey, {
          past: [...mediaHistory.past, current],
          present: [],
          future: [],
        });
      });
    },

    undo: () => {
      const state = get();

      if (!state.activeMediaSetId || !state.activeMediaKey) {
        console.error("Cannot undo: no active media set");
        return;
      }

      const mediaKey = generateMediaKey(state.activeMediaSetId, state.activeMediaKey);
      const mediaHistory = state.history.get(mediaKey);

      if (!mediaHistory || mediaHistory.past.length === 0) return;

      const previous = mediaHistory.past[mediaHistory.past.length - 1];
      const newPast = mediaHistory.past.slice(0, -1);

      set((state) => {
        state.annotationsByMedia.set(mediaKey, previous!);
        state.history.set(mediaKey, {
          past: newPast,
          present: previous!,
          future: [mediaHistory.present, ...mediaHistory.future],
        });
      });
    },

    redo: () => {
      const state = get();

      if (!state.activeMediaSetId || !state.activeMediaKey) {
        console.error("Cannot redo: no active media set");
        return;
      }

      const mediaKey = generateMediaKey(state.activeMediaSetId, state.activeMediaKey);
      const mediaHistory = state.history.get(mediaKey);

      if (!mediaHistory || mediaHistory.future.length === 0) return;

      const next = mediaHistory.future[0];
      const newFuture = mediaHistory.future.slice(1);

      set((state) => {
        state.annotationsByMedia.set(mediaKey, next!);
        state.history.set(mediaKey, {
          past: [...mediaHistory.past, mediaHistory.present],
          present: next!,
          future: newFuture,
        });
      });
    },

    canUndo: () => {
      const state = get();

      if (!state.activeMediaSetId || !state.activeMediaKey) {
        return false;
      }

      const mediaKey = generateMediaKey(state.activeMediaSetId, state.activeMediaKey);
      const mediaHistory = state.history.get(mediaKey);

      return mediaHistory ? mediaHistory.past.length > 0 : false;
    },

    canRedo: () => {
      const state = get();

      if (!state.activeMediaSetId || !state.activeMediaKey) {
        return false;
      }

      const mediaKey = generateMediaKey(state.activeMediaSetId, state.activeMediaKey);
      const mediaHistory = state.history.get(mediaKey);

      return mediaHistory ? mediaHistory.future.length > 0 : false;
    },

    setAnnotationsForMedia: (mediaSetId, mediaKey, annotations) => {
      const compositeKey = `${mediaSetId}:${mediaKey}`;

      // Clean all annotation values
      const cleanedAnnotations = annotations.map((ann) => cleanAnnotationValues(ann));

      set((state) => {
        state.annotationsByMedia.set(compositeKey, cleanedAnnotations);
        state.history.set(compositeKey, {
          past: [],
          present: cleanedAnnotations,
          future: [],
        });
      });
    },
  })),
);
